import {
  DecimalPipe,
  formatDate,
  getCurrencySymbol,
  NgIf,
  NgTemplateOutlet,
  NgClass,
  AsyncPipe,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent, PrintPageComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { Discount } from 'src/app/settings/models/settings.model';
import { getDiscount } from 'src/app/settings/store/selectors/settings.selectors';
import { EXTENDED_SIMULATION_VIEWABLE_VARIABLES_TABLE } from 'src/assets/data/feedProfitConstants';

import { ExtendedSimulationItemComponent } from './extended-simulation-item/extended-simulation-item.component';
import { ExtendedSimulationReportComponent } from './extended-simulation-report/extended-simulation-report.component';
import { ExtendedSimulationTableComponent } from './extended-simulation-table/extended-simulation-table.component';
import {
  Calculation,
  ExtendedSimulation,
  ExtendedSimulationData,
  ExtendedSimulationExtendedBaseLine,
  ExtendedSimulationFeederComparison,
  ExtendedSimulationFullObject,
  ExtendedSimulationTableRow,
  Feeder,
  FeederType,
  GenderOptions,
  PriceSetting,
} from '../../models/feed-profit.model';
import { FeedProfitCalculations } from '../../services/feed-profit-calculations.service';
import { FeedProfitExtendedSimulationsCalculationService } from '../../services/feed-profit-extended-simulations-calculation.service';
import * as actions from '../../store/feed-profit.actions';
import { getSelectedCalculation } from '../../store/feed-profit.selectors';

@Component({
  selector: 'app-extended-simulation',
  templateUrl: './extended-simulation.component.html',
  styleUrls: ['./extended-simulation.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    NgTemplateOutlet,
    MatDivider,
    ExtendedSimulationTableComponent,
    MatIcon,
    TableComponent,
    NgClass,
    PrintPageComponent,
    ExtendedSimulationReportComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ExtendedSimulationComponent implements AfterViewInit, OnInit {
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  calculationDataSource: ExtendedSimulationTableRow[];
  feedersDataSource: ExtendedSimulationFeederComparison[];

  public getExtendedSimulationTableRows(): Observable<ExtendedSimulationTableRow[]> {
    return of(this.calculationDataSource);
  }

  public getfeedersDataSource(): Observable<ExtendedSimulationFeederComparison[]> {
    return of(this.feedersDataSource);
  }

  @ViewChild('comparisonSimColumn') comparisonSimColumn: TemplateRef<unknown>;
  @ViewChild('extendedSimColumn') extendedSimColumnTemplate: TemplateRef<unknown>;
  @ViewChild('feederColumn') feederColumnTemplate: TemplateRef<unknown>;

  hasExtendedSimulation: boolean;

  extendedSimulationData$: Observable<ExtendedSimulationData>;

  quickSimulationsCanbeCalculated: boolean;

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  feederColumns: TableColumn[] = undefined;
  currencySymbol: string;

  calculation: Calculation;

  private readonly feederTableBase: TableConfig = {
    dataSource: new MatTableDataSource([]),
    columns: this.feederColumns,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
    width: '2000px',
  };

  currentFeedersTableConfig$: Observable<TableConfig>;
  simulationFeedersTableConfig$: Observable<TableConfig>;

  columns: TableColumn[] = undefined;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: this.columns,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };
  public tableConfig$: Observable<TableConfig>;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly decimalPipe: DecimalPipe,
    private readonly calculationService: FeedProfitExtendedSimulationsCalculationService,
    private readonly genericCalculationService: FeedProfitCalculations,
    private readonly dialog: DialogService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.currencySymbol = getCurrencySymbol(this.localeService.getCustomerCurrency(), 'narrow');

    this.extendedSimulationData$ = this.store.select(getSelectedCalculation)?.pipe(
      skipWhile((calculation) => !calculation),
      map((calculation) =>
        calculation !== null && calculation !== undefined
          ? <Calculation>{
              ...calculation,
              // Create extended comparison object from calculation values
              extendedComparisonSimulation: this.getComparisonSimulation(
                calculation,
                this.quickSimulationsCanbeCalculated,
              ),
            }
          : null,
      ),
      map((calculation: Calculation) => {
        this.calculation = calculation;

        if (calculation === null || calculation === undefined) {
          return;
        }

        this.hasExtendedSimulation = this.genericCalculationService.getHasExtendedSimulation(
          this.calculation,
        );

        let comparisonBaseSimulation;
        if (
          calculation &&
          this.genericCalculationService.getQuickSimulationCanBeCalculated(calculation)
        ) {
          comparisonBaseSimulation = this.calculationService.calculateExtendedSimulation(
            calculation,
            calculation.extendedComparisonSimulation,
            0,
          );
        }
        let extendedSimulation;
        if (this.genericCalculationService.getExtendedSimulationCanBeCalculated(calculation)) {
          extendedSimulation = this.calculationService.calculateExtendedSimulation(
            calculation,
            calculation.extendedSimulation,
            1,
          );
        } else {
          comparisonBaseSimulation = this.mapEmptySimulation(
            calculation.extendedComparisonSimulation,
          );
          extendedSimulation = this.mapEmptySimulation(calculation.extendedSimulation);
        }
        return {
          comparison: comparisonBaseSimulation,
          extended: extendedSimulation,
        };
      }),
    );
  }

  ngAfterViewInit() {
    this.tableConfig$ = this.extendedSimulationData$?.pipe(
      map((extendedSimulationData) => {
        const tableConfig: TableConfig = {
          ...this.tableConfigBase,
        };
        tableConfig.columns = <TableColumn[]>[
          {
            id: 'type',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.detail.extended-simulation.type'),
            disableSort: true,
          },
          {
            id: 'comparisonBaseSimulation',
            type: TableColumnTypes.TEMPLATE,
            label: this.localeService.translate(
              'feed-profit.detail.extended-simulation.base-simulation',
            ),
            disableSort: true,
            digitsInfo: '1.01-02',
            template: this.comparisonSimColumn,
          },
          {
            id: 'extendedSimulation',
            type: TableColumnTypes.TEMPLATE,
            label: this.localeService.translate(
              'feed-profit.detail.extended-simulation.extended-simulation',
            ),
            disableSort: true,
            digitsInfo: '1.01-02',
            template: this.extendedSimColumnTemplate,
          },
        ];

        let dataSourceInput = [extendedSimulationData?.comparison];
        if (extendedSimulationData?.comparison != null) {
          dataSourceInput = [extendedSimulationData.comparison, extendedSimulationData.extended];

          this.calculationDataSource = this.createCalculationsDatasource(dataSourceInput);

          tableConfig.dataSource = new MatTableDataSource(this.calculationDataSource);
        } else {
          tableConfig.dataSource = new MatTableDataSource([]);
        }

        return tableConfig;
      }),
    );

    const feederColumns = <TableColumn[]>[
      {
        id: 'Type',
        type: TableColumnTypes.STRING,
        label: this.localeService.translate('feed-profit.detail.extended-simulation.feeders.type'),
        disableSort: false,
      },
      {
        id: 'Code',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate('feed-profit.detail.extended-simulation.feeders.code'),
        disableSort: false,
        template: this.feederColumnTemplate,
      },
      {
        id: 'EnergyValues',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate('feed-profit.detail.extended-simulation.feeders.ew'),
        disableSort: false,
        digitsInfo: '1.01-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'Lysine',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate(
          'feed-profit.detail.extended-simulation.feeders.Il-lys',
        ),
        disableSort: false,
        digitsInfo: '1.01-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'Protein',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate(
          'feed-profit.detail.extended-simulation.feeders.protein',
        ),
        disableSort: false,
        digitsInfo: '1.01-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'Re',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate('feed-profit.detail.extended-simulation.feeders.re'),
        disableSort: false,
        digitsInfo: '1.01-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'GrossPrice',
        type: TableColumnTypes.TEMPLATE,
        label:
          this.localeService.translate(
            'feed-profit.detail.extended-simulation.feeders.gross-price',
          ) + ` (${this.currencySymbol})`,
        disableSort: false,
        digitsInfo: '1.02-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'NetPrice',
        type: TableColumnTypes.TEMPLATE,
        label:
          this.localeService.translate('feed-profit.detail.extended-simulation.feeders.net-price') +
          ` (${this.currencySymbol})`,
        disableSort: false,
        digitsInfo: '1.02-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'KgFeed',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate(
          'feed-profit.detail.extended-simulation.feeders.kg-feed',
        ),
        disableSort: false,
        digitsInfo: '1.01-02',
        template: this.feederColumnTemplate,
      },
      {
        id: 'FeedCosts',
        type: TableColumnTypes.TEMPLATE,
        label:
          this.localeService.translate(
            'feed-profit.detail.extended-simulation.feeders.feed-costs',
          ) + ` (${this.currencySymbol})`,
        disableSort: false,
        digitsInfo: '1.02-02',
        template: this.feederColumnTemplate,
      },
    ].filter((c) =>
      this.calculation?.companyDetails?.priceSetting === PriceSetting.BRUTO
        ? c.id !== 'NetPrice'
        : 'GrossPrice',
    );

    this.currentFeedersTableConfig$ = this.store.select(getDiscount).pipe(
      skipWhile((discount) => !discount),
      map((discount) => {
        if (this.calculation) {
          return {
            ...this.feederTableBase,
            columns: feederColumns,
            dataSource:
              this.calculation?.calculatedFeeder || this.calculation.feeders?.length > 0
                ? this.calculation?.extendedSimulation?.calculatedFeeder ||
                  this.calculation?.extendedSimulation?.feeders?.length > 0
                  ? this.createFeedersDataSource(
                      [...(this.calculation.feeders ?? []), this.calculation.calculatedFeeder],
                      discount,
                      [
                        ...(this.calculation.extendedSimulation.feeders ?? []),
                        this.calculation.extendedSimulation.calculatedFeeder,
                      ],
                    )
                  : this.createFeedersDataSource(
                      [...this.calculation.feeders, this.calculation.calculatedFeeder],
                      discount,
                    )
                : new MatTableDataSource([]),
          };
        }
      }),
    );

    this.changeDetectorRef.detectChanges();
  }

  // Need to use this sub method since pipes are not allowed in the primary action binding of the toolbar
  addOrEditExtendedSimulation() {
    if (this.hasExtendedSimulation) {
      this.editExtendedSimulation();
    } else {
      this.addExtendedSimulation();
    }
  }

  addExtendedSimulation() {
    //provide defaults to new calculation
    const newExtendedSimulation = <ExtendedSimulation>{
      id: 0,
      ...this.calculation.extendedSimulation,
      feeders: [],
      calculatedFeeder: null,
    };
    const newCalculation = <Calculation>{
      ...this.calculation,
      extendedSimulation: newExtendedSimulation,
    };
    this.dialog
      ?.fullscreenDialog(
        ExtendedSimulationItemComponent,
        {
          title: this.localeService?.translate('feed-profit.detail.extended-simulation.item-title'),
          subTitle: this.localeService?.translate(
            'feed-profit.detail.extended-simulation.item-subtitle',
          ),
          confirmButton: true,
          confirmButtonText: this.localeService?.translate('save'),
          calculation: newCalculation,
          pushHistoryState: false,
          fullscreen: true,
        },
        {
          maxWidth: '70vw',
          maxHeight: '100vh',
          height: '100%',
          width: '70%',
        },
      )
      ?.subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          const returnedCalculation = result.result.calculation
            ? result.result.calculation
            : this.calculation;

          this.updateSaveData(returnedCalculation);
          this.store.dispatch(actions.setIsChanged({ isChanged: false }));
        }
      });
  }

  updateSaveData(calculation: Calculation) {
    this.store.dispatch(
      actions.upsertCalculationRequest({
        calculation,
      }),
    );
    this.store.dispatch(
      actions.updateSelectedCalculation({
        values: {
          ...calculation,
        },
      }),
    );
  }

  editExtendedSimulation() {
    this.dialog
      .fullscreenDialog(
        ExtendedSimulationItemComponent,
        {
          title: this.localeService.translate('feed-profit.detail.extended-simulation.item-title'),
          subTitle: this.localeService.translate(
            'feed-profit.detail.extended-simulation.item-subtitle',
          ),
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          calculation: this.calculation,

          pushHistoryState: false,
          fullscreen: true,
        },
        {
          maxWidth: '70vw',
          maxHeight: '100vh',
          height: '100%',
          width: '70%',
        },
      )
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          const returnedCalculation = result.result.calculation
            ? result.result.calculation
            : this.calculation;

          this.updateSaveData(returnedCalculation);
          this.store.dispatch(actions.setIsChanged({ isChanged: false }));
        }
      });
  }

  createCalculationsDatasource(dataSourceInput: ExtendedSimulationFullObject[]) {
    const comparisonData = Object.entries(dataSourceInput[0]).filter(
      (entry) => entry[0] !== 'id' && entry[0] !== 'isComparisonSim',
    );
    const extendedData = Object.entries(dataSourceInput[1]).filter(
      (entry) => entry[0] !== 'id' && entry[0] !== 'isComparisonSim',
    );
    const data: ExtendedSimulationTableRow[] = [];

    // Combine both datasources and map from horizontal display to vertical
    comparisonData.forEach((comparisonData) => {
      extendedData.forEach((extendedData) => {
        if (comparisonData[0] === extendedData[0]) {
          data.push({
            variableName: comparisonData[0],
            type: this.localeService.translate(
              'feed-profit.detail.extended-simulation.data.' + comparisonData[0],
            ),
            comparisonBaseSimulation: comparisonData[1],
            extendedSimulation: extendedData[1],
            hasCurrency: false,
          });
        }
      });
    });
    // Not all simulation calculation results are shown in the table, this ensures that.
    return data.filter((item) =>
      EXTENDED_SIMULATION_VIEWABLE_VARIABLES_TABLE.includes(item.variableName),
    );
  }

  createFeedersDataSource(currentData: Feeder[], discounts: Discount, extendedData?: Feeder[]) {
    this.feedersDataSource = [
      ...this.mapFeederType(FeederType.START_FEEDER, discounts, currentData, extendedData),
      ...this.mapFeederType(FeederType.GROWTH_FEEDER, discounts, currentData, extendedData),
      ...this.mapFeederType(FeederType.END_FEEDER, discounts, currentData, extendedData),
    ];

    //format feeders to recognizeable order
    return new MatTableDataSource(this.feedersDataSource);
  }

  mapFeederType(
    type: FeederType,
    discounts: Discount,
    currentFeedersInput?: Feeder[],
    simulationFeedersInput?: Feeder[],
  ) {
    const currentFeeders = currentFeedersInput
      ? [...currentFeedersInput.filter((f) => f && f.type === type)]
      : [];
    const simulationFeeders = simulationFeedersInput
      ? [...simulationFeedersInput.filter((f) => f && f.type === type)]
      : [];
    const mappedFeeders = [];

    const biggestLength =
      currentFeeders?.length > simulationFeeders?.length
        ? currentFeeders?.length
        : simulationFeeders?.length || 0;

    for (let i = 0; i < biggestLength; i++) {
      const currentFeeder = currentFeeders[i];
      const simulationFeeder = simulationFeeders[i];
      const currentAvgKgFeed =
        currentFeeder?.genderFeedRatios?.map((r) => r.feed).reduce((prev, cur) => prev + cur) /
        currentFeeder?.genderFeedRatios?.length;
      const simulationAvgKgFeed =
        simulationFeeder?.genderFeedRatios?.map((r) => r.feed).reduce((prev, cur) => prev + cur) /
        simulationFeeder?.genderFeedRatios?.length;

      mappedFeeders.push({
        Type: this.localeService.translate(
          `feed-profit.detail.extended-simulation.feeders.${type.toLocaleLowerCase()}-type`,
        ),
        curCode: currentFeeder?.product?.code,
        extCode: simulationFeeder?.product?.code,
        curEnergyValues: currentFeeder?.product?.ewKg,
        extEnergyValues: simulationFeeder?.product?.ewKg,
        curLysine: currentFeeder?.product?.lysKg,
        extLysine: simulationFeeder?.product?.lysKg,
        curProtein: currentFeeder?.product?.proteinGramPerKg,
        extProtein: simulationFeeder?.product?.proteinGramPerKg,
        curRe: currentFeeder?.product?.phosphorusGramPerKg,
        extRe: simulationFeeder?.product?.phosphorusGramPerKg,
        curGrossPrice: currentFeeder?.product?.grossPricePer100Kg,
        extGrossPrice: simulationFeeder?.product?.grossPricePer100Kg,
        curNetPrice: this.genericCalculationService.getProductPriceWithDiscounts(
          currentFeeder?.product?.grossPricePer100Kg,
          discounts,
        ),
        extNetPrice: this.genericCalculationService.getProductPriceWithDiscounts(
          simulationFeeder?.product?.grossPricePer100Kg,
          discounts,
        ),
        curKgFeed: currentAvgKgFeed,
        extKgFeed: simulationAvgKgFeed,
        curFeedCosts: currentFeeder?.product?.grossPricePer100Kg * currentAvgKgFeed,
        extFeedCosts: simulationFeeder?.product?.grossPricePer100Kg * simulationAvgKgFeed,
      });
    }
    return mappedFeeders;
  }

  getComparisonSimulation(calculation: Calculation, canBeCalculated = false) {
    // first create the initial object
    let tempComparison: ExtendedSimulation = {
      genders: calculation?.genders,
      country: calculation?.country,
      startWeight: calculation?.extendedBaseLine?.startWeight,
      pigletPrice: calculation?.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: calculation?.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: calculation?.extendedBaseLine?.revenuePerKg,
      feedPackagePrice: 25.41,

      feeders: calculation?.feeders?.length > 0 ? calculation?.feeders : null,
      calculatedFeeder: calculation?.calculatedFeeder,
      baseLine: calculation?.baseLine,
      extendedBaseLine: <ExtendedSimulationExtendedBaseLine>{
        ...calculation?.extendedBaseLine,
        feedPackagePrice: 25.41,
      },
    };
    if (canBeCalculated) {
      // add feedpackageprice
      const feedPackagePrice = this.calculationService.getFeedPackagePrice(tempComparison);
      tempComparison = {
        ...tempComparison,
        feedPackagePrice,
        extendedBaseLine: {
          ...tempComparison.extendedBaseLine,
          feedPackagePrice,
        },
      };
      // lastly, add the calculatedfeeder
      const calculatedFeederRatios = this.calculationService.calculateRemainingEndFeederRatios(
        calculation,
        tempComparison,
      );
      return <ExtendedSimulation>{
        ...tempComparison,
        calculatedFeeder: {
          ...tempComparison.calculatedFeeder,
          genderFeedRatios: [
            {
              name: GenderTypes.GILT,
              feed: calculatedFeederRatios.gilt ?? 0,
            },
            calculation.genders === GenderOptions.BOAR_GILT
              ? {
                  name: GenderTypes.BOAR,
                  feed: calculatedFeederRatios.boar ?? 0,
                }
              : {
                  name: GenderTypes.BARROW,
                  feed: calculatedFeederRatios.barrow ?? 0,
                },
          ],
        },
      };
    }
    return tempComparison;
  }

  mapEmptySimulation(simulation: ExtendedSimulation) {
    return <ExtendedSimulationFullObject>{
      ...simulation,
      startWeight: simulation?.extendedBaseLine?.startWeight,
      pigletPrice: simulation?.extendedBaseLine?.growFinisherPrice,
      slaughteredWeightInKg: simulation?.extendedBaseLine?.slaughteredWeightInKg,
      revenuePerKg: simulation?.extendedBaseLine?.revenuePerKg,
      feedPackagePrice: simulation?.extendedBaseLine?.feedPackagePrice,
      isComparisonSim: false,
      occupancyRate: null,
      amount: null,
      growth: simulation?.extendedBaseLine?.growth,
      feedConversion: simulation?.extendedBaseLine?.averageEnergyConversion,
      feedPrice: simulation?.extendedBaseLine?.feedPackagePrice,
      finalDifferenceDelivered: null,
      finalDifferenceAveragePresentGrowFinisher: null,
      finalDifferenceYear: null,
      feedProfitDelivered: null,
      feedProfitYear: null,
      feedProfitAverage: null,

      averageAmountOfPresentDeliveredGrowFinishers: null,
      divergingDaysStartWeight: null,
      divergingDaysSlaughteredWeight: null,
      divergingDaysGrowth: null,
      amountOfDays: null,
      meatPercentage: simulation?.extendedBaseLine?.meatPercentage,
      mortalityPercentage: simulation?.extendedBaseLine?.mortalityPercentage,
      milimeterMuscle: null,
      milimeterNeck: simulation?.extendedBaseLine?.backFatThicknessInMillimeters,
      amountOfRounds: null,
      divergingEnergyFromGrowthStartStartWeight: null,
      divergingEnergyFromGrowthEndDeliveredWeight: null,
      divergingEnergyDivergingGrowth: null,
      energyConversion: simulation?.extendedBaseLine?.averageEnergyConversion,
      energyConversionBoarOrBarrow: null,
      energyConversionGilt: null,
      effectDifferentStartWeightOnFeedCosts: null, //J26
      effectSlaughteredWeightOnFeedCost: null,
      effectDifferenceGrowthOnFeedCost: null,
      energyUse: null,
      feedCostPerKgPerGrowth: null,
      feedProfitPerDeliveredGrowFinisher: null,
      profitPerGrowFinisher: null,
      feedCosts: null,
      costDeathsAndSpoilage: null,

      squareMeterPerGrowFinisher: simulation?.extendedBaseLine?.squareMeterPerGrowFinisher,
      fertilizerPricePerCubicMeter: simulation?.extendedBaseLine?.fertilizerPricePerCubicMeter,
      fertilizerCostsPerGrowFinisher: null,
      feedProfitAverageGrowFinisherInclFertilizer: null,
      feedProfitPerSquareMeterInclFertilizer: null,
      comparisonSimFeedProfitPerSquareMeterIncludingFertilizer: null,
    };
  }

  getValue(value: unknown, digitsInfo = '1.0-2') {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      isNaN(parseFloat(value as string))
    ) {
      return this.localeService.translate('n/a');
    }
    return this.decimalPipe.transform(
      value as unknown as string,
      digitsInfo,
      this.localeService.getLocale(),
    );
  }

  getSummaryValue(value: number, digitsInfo = '1.0-2') {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return this.localeService.translate('-');
    }
    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  getData(value: number, comparisonValue: number, digits = '1.0-2') {
    const diff = this.getSummaryDifference(value, comparisonValue, digits);

    return {
      simulationVal: this.getSummaryValue(value),
      comparisonVal: this.getSummaryValue(comparisonValue),
      difference: diff,
      digitsInfo: digits,
      positive: this.isPositive(diff),
    };
  }

  getSummaryDifference(comparison: number, extended: number, digitsInfo = '1.0-2') {
    const summaryValue = extended - comparison;

    return this.getSummaryValue(summaryValue, digitsInfo);
  }

  getFeedersValue(value: string, digitsInfo = '1.0-2', columnType?: string) {
    if (value === undefined || value === null) {
      return null;
    }
    // check if we should not parse the value
    if (columnType && (columnType === 'Type' || columnType === 'Code')) {
      return value;
    }
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue) || !isFinite(parsedValue)) {
      return null;
    }
    return this.decimalPipe.transform(value, digitsInfo, this.localeService.getLocale());
  }

  getFeedersDifference(comparison: number, extended: number, digitsInfo = '1.0-2') {
    const summaryValue = extended - comparison;

    return this.getSummaryValue(summaryValue, digitsInfo);
  }

  getDifferenceValue(value: ExtendedSimulationTableRow, digitsInfo = '1.0-2') {
    const summaryValue =
      parseFloat(value.extendedSimulation) - parseFloat(value.comparisonBaseSimulation);

    return this.getSummaryValue(summaryValue, digitsInfo);
  }

  isPositive(text: string): number {
    const parsedText = parseFloat(text?.replace(',', '.')) * 100;
    return parsedText > 0 ? 1 : parsedText < 0 ? -1 : 0;
  }

  getPrimaryActionIcon() {
    return this.hasExtendedSimulation ? 'edit' : 'add';
  }

  getPrimaryActionText() {
    return this.hasExtendedSimulation
      ? this.localeService.translate('feed-profit.detail.extended-simulation.edit-simulation')
      : this.localeService.translate('feed-profit.detail.extended-simulation.add-simulation');
  }

  printExtendedSimulation() {
    const existingTitle = document.title;
    const proposedTitle = `Pig ${this.localeService.translate(
      'feed-profit.detail.extended-simulation.title',
    )} - ${formatDate(Date(), 'dd-MM-yyyy', this.localeService.getLocale())}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }
}
